import { useState, useEffect, useCallback } from 'react';
import { Select, Checkbox, Tag } from 'antd';
import useFilterLookups from '@/hooks/useFilterLookups';
import styles from '../NodeCustomization.module.css';
import { CustomizationComponent } from '@/pages/Logiblocks/types';
import LocationFilter from './LocationFilter';
import type { SelectedLocation } from './LocationFilter';

const { Option } = Select;

/**
 * Customization UI for FILTERED_SHIPMENTS_LOOP nodes.
 * Allows users to filter shipments by status, origin, and destination.
 */
export const Deprecated_FilteredShipmentsLoopCustomization: CustomizationComponent =
  ({ nodeData, onChange, isReadOnly = false }) => {
    const lookups = useFilterLookups();
    const { shipmentStatusDDL, loaders } = lookups;
    const locationsFetcher = lookups.fetchers?.grouped_locations;

    const [statusLoading, setStatusLoading] = useState(false);

    // Extract current filter values from nodeData
    const currentStatusIds =
      nodeData.deprecated_filtered_shipments_loop?.status_ids || [];
    const [selectedStatusIds, setSelectedStatusIds] =
      useState<string[]>(currentStatusIds);

    const currentOriginIds =
      nodeData.deprecated_filtered_shipments_loop?.origin_location_ids || [];
    const [selectedOrigins, setSelectedOrigins] = useState<SelectedLocation[]>(
      currentOriginIds.map((id) => ({ id, name: id }))
    );

    const currentDestIds =
      nodeData.deprecated_filtered_shipments_loop?.destination_location_ids ||
      [];
    const [selectedDestinations, setSelectedDestinations] = useState<
      SelectedLocation[]
    >(currentDestIds.map((id) => ({ id, name: id })));

    // Load shipment statuses on mount
    useEffect(() => {
      setStatusLoading(true);
      loaders.shipmentStatus().finally(() => {
        setStatusLoading(false);
      });
    }, [loaders]);

    const statusOptions = shipmentStatusDDL;

    // Sync local state when nodeData changes (e.g., node switching)
    useEffect(() => {
      const statusIds =
        nodeData.deprecated_filtered_shipments_loop?.status_ids || [];
      setSelectedStatusIds(statusIds);

      const originIds =
        nodeData.deprecated_filtered_shipments_loop?.origin_location_ids || [];
      setSelectedOrigins((prev) => {
        const prevMap = new Map(prev.map((l) => [l.id, l]));
        return originIds.map((id) => prevMap.get(id) ?? { id, name: id });
      });

      const destIds =
        nodeData.deprecated_filtered_shipments_loop?.destination_location_ids ||
        [];
      setSelectedDestinations((prev) => {
        const prevMap = new Map(prev.map((l) => [l.id, l]));
        return destIds.map((id) => prevMap.get(id) ?? { id, name: id });
      });
    }, [nodeData]);

    /** Build the full data object and call onChange. */
    const emitChange = useCallback(
      (overrides: {
        statusIds?: string[];
        originLocationIds?: string[];
        destinationLocationIds?: string[];
      }) => {
        onChange({
          deprecated_filtered_shipments_loop: {
            subgraph_index:
              nodeData.deprecated_filtered_shipments_loop?.subgraph_index ?? -1,
            status_ids: overrides.statusIds ?? selectedStatusIds,
            origin_location_ids:
              overrides.originLocationIds ?? selectedOrigins.map((l) => l.id),
            destination_location_ids:
              overrides.destinationLocationIds ??
              selectedDestinations.map((l) => l.id),
          },
        });
      },
      [
        nodeData,
        onChange,
        selectedStatusIds,
        selectedOrigins,
        selectedDestinations,
      ]
    );

    const handleStatusChange = (values: string[]) => {
      let updatedStatusIds: string[];

      if (values.includes('ALL')) {
        if (selectedStatusIds.length === statusOptions.length) {
          updatedStatusIds = [];
        } else {
          updatedStatusIds = statusOptions.map((s) => s.id);
        }
      } else {
        updatedStatusIds = values.filter((v) => v !== 'ALL');
      }

      setSelectedStatusIds(updatedStatusIds);
      emitChange({ statusIds: updatedStatusIds });
    };

    const handleOriginsChange = useCallback(
      (locations: SelectedLocation[]) => {
        setSelectedOrigins(locations);
        emitChange({ originLocationIds: locations.map((l) => l.id) });
      },
      [emitChange]
    );

    const handleDestinationsChange = useCallback(
      (locations: SelectedLocation[]) => {
        setSelectedDestinations(locations);
        emitChange({ destinationLocationIds: locations.map((l) => l.id) });
      },
      [emitChange]
    );

    return (
      <>
        {/* Status filter */}
        <div className={styles.customizationSection}>
          <label className={styles.sectionLabel}>Select Shipment Status</label>
          {statusLoading ? (
            <div>Loading statuses...</div>
          ) : statusOptions.length === 0 ? (
            <div>No statuses available</div>
          ) : (
            <Select
              mode='multiple'
              placeholder='Select statuses...'
              value={selectedStatusIds}
              onChange={handleStatusChange}
              className={styles.filterDropdown}
              style={{
                background: 'var(--light-gray)',
                border: 'none',
                boxShadow: 'none',
              }}
              optionLabelProp='label'
              disabled={isReadOnly}
              tagRender={({ label, value }) => {
                if (selectedStatusIds.length === statusOptions.length) {
                  if (value === selectedStatusIds[0]) {
                    return (
                      <Tag className={styles.filterTag} closable>
                        All
                      </Tag>
                    );
                  }
                  return null;
                }
                return (
                  <Tag className={styles.filterTag} closable>
                    {label}
                  </Tag>
                );
              }}
            >
              {/* Select All option */}
              <Option key='all' value='ALL' label='All'>
                <Checkbox
                  className={styles.filterCheckbox}
                  checked={selectedStatusIds.length === statusOptions.length}
                  indeterminate={
                    selectedStatusIds.length > 0 &&
                    selectedStatusIds.length < statusOptions.length
                  }
                >
                  All
                </Checkbox>
              </Option>

              {/* Individual status options */}
              {statusOptions.map((status) => (
                <Option
                  key={status.id}
                  value={status.id}
                  label={status.name || status.id}
                >
                  <Checkbox
                    className={styles.filterCheckbox}
                    checked={selectedStatusIds.includes(status.id)}
                  >
                    {status.name || status.id}
                  </Checkbox>
                </Option>
              ))}
            </Select>
          )}
        </div>

        {/* Origin location filter */}
        {locationsFetcher && (
          <LocationFilter
            label='Select Origin'
            fetcher={locationsFetcher}
            selectedLocations={selectedOrigins}
            onChangeLocations={handleOriginsChange}
            isReadOnly={isReadOnly}
          />
        )}

        {/* Destination location filter */}
        {locationsFetcher && (
          <LocationFilter
            label='Select Destination'
            fetcher={locationsFetcher}
            selectedLocations={selectedDestinations}
            onChangeLocations={handleDestinationsChange}
            isReadOnly={isReadOnly}
          />
        )}
      </>
    );
  };
